import {
  drybulkCargoOperationsService,
  type CargoOperationDetail,
  type OperationSequencesResponse,
  type SequenceRequest,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { DRYBULK_CARGO_OPS_QUERY_KEYS, DRYBULK_CARGO_OPS_TELEMETRY } from "../constants";
import { problemStatus } from "../utils/problemDetail";

const context = (action: string, operationId: string) => ({
  module: DRYBULK_CARGO_OPS_TELEMETRY.module,
  screen: DRYBULK_CARGO_OPS_TELEMETRY.sequencesScreen,
  action,
  entityType: "cargo_operation",
  entityId: operationId,
});

/**
 * DBCO-0029 (R-04, R-48) - add, update and remove a sequence. Each is one business transaction
 * (telemetry id forwarded to the API call). On success the server's sequences and Total Quantity
 * Remained are shown at once (no client arithmetic), and the detail is refetched.
 */
export const useSequenceMutations = (operationId: string) => {
  const queryClient = useQueryClient();
  const detailKey = DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId);

  const onSuccess = (response: OperationSequencesResponse) => {
    queryClient.setQueryData<CargoOperationDetail>(detailKey, (detail) =>
      detail
        ? {
            ...detail,
            sequences: response.sequences,
            totalQuantityRemained: response.totalQuantityRemained,
          }
        : detail,
    );
    void queryClient.invalidateQueries({ queryKey: detailKey });
    // DBCO-0031: a sequence change moves the grid row's gradeSizes.
    void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
  };

  // 409: the operation moved on (approved meanwhile, or no Total Quantity) - the page must refresh.
  const onError = (error: unknown) => {
    if (problemStatus(error) === 409) {
      void queryClient.invalidateQueries({ queryKey: detailKey });
    }
  };

  const add = useMutation({
    mutationFn: (request: SequenceRequest) =>
      runTransaction(context("add_cargo_operation_sequence", operationId), (transactionId) =>
        drybulkCargoOperationsService.addSequence(operationId, request, { transactionId }),
      ),
    onSuccess,
    onError,
  });

  const update = useMutation({
    mutationFn: ({ sequenceId, request }: { sequenceId: string; request: SequenceRequest }) =>
      runTransaction(context("update_cargo_operation_sequence", operationId), (transactionId) =>
        drybulkCargoOperationsService.updateSequence(operationId, sequenceId, request, {
          transactionId,
        }),
      ),
    onSuccess,
    onError,
  });

  const remove = useMutation({
    mutationFn: (sequenceId: string) =>
      runTransaction(context("remove_cargo_operation_sequence", operationId), (transactionId) =>
        drybulkCargoOperationsService.removeSequence(operationId, sequenceId, { transactionId }),
      ),
    onSuccess,
    onError,
  });

  return { add, update, remove };
};
